import {
    PRODUCT_ID_CITY_BREAK_TICKET_ADULT,
    PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER,
    PRODUCT_ID_MARCH_VOUCHER_PROLONGED,
    PRODUCT_ID_MARCH_VOUCHER_STANDARD,
    PRODUCT_TYPE_BUNDLE,
    PRODUCT_TYPE_TICKET,
} from 'constants/app'
import {
    CartItemTicketDetailDto,
    GetCartItemPresenter,
} from 'store/api-slice/hooks'
import { MarchVoucher, Ticket } from 'store/api-slice/types'

// check if ticket is dedicated for any existing promotion
// - basically for filtering out special tickets out of regular ones
const isTicketPromotional = (productId?: string) =>
    productId &&
    [
        PRODUCT_ID_CITY_BREAK_TICKET_ADULT,
        PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER,
    ].includes(productId)

// filters data from cart api, searching for:
// - tickets and bundles treated as regular tickets to park
export const filterTicketsWithBundlesFromCart = (
    cartItems?: GetCartItemPresenter[]
): Ticket[] => {
    if (!cartItems) return []

    // regular tickets to park
    const tickets = cartItems
        ?.filter((cartItem: GetCartItemPresenter) => {
            const { productType, productId } = cartItem

            // cart item:
            // - has to be 'ticket' type
            // - can't be promotional ticket
            return (
                productType === PRODUCT_TYPE_TICKET &&
                !isTicketPromotional(productId)
            )
        })
        .map(({ detail, ...rest }) => ({
            ...rest,
            detail: detail as CartItemTicketDetailDto,
        }))

    // tickets to park bundled with other products, but within app logic treated as standard tickets
    // === senior package
    const ticketsBundles = cartItems
        ?.filter((cartItem: GetCartItemPresenter) => {
            const { productType, itemsBundle, name } = cartItem

            // - cart item has to be 'bundle' type
            if (productType !== PRODUCT_TYPE_BUNDLE) return false

            // TODO: figure out with backend how to add meningful key ids to different bundles,
            // - actually, bundle name has to include 'senior'
            // - ??? maybe move this into some helepr function (along with reckognizing family package)
            if (
                !name?.toLowerCase().includes('senior') &&
                !name?.toLowerCase().includes('сеньора')
            )
                return false

            // - and has to contain some tickets within its bundled items
            const hasBundledTicket = !!itemsBundle?.some(
                ({ productType }) => productType === PRODUCT_TYPE_TICKET
            )

            return hasBundledTicket
        })
        .map((bundle) => {
            const { itemsBundle, productId } = bundle
            const bundledTicket = itemsBundle?.find(
                ({ productType }) => productType === PRODUCT_TYPE_TICKET
            )

            if (bundledTicket) {
                const { cartItemId, detail } = bundledTicket || {}
                const bundleDetail: CartItemTicketDetailDto = {
                    ...detail,
                    // TODO: fix/extend types when there is more time / with selfcare maybe
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    ticketTime: productId,
                    // above line is not mistake,
                    // it's workaround for reckognizing senior package on refresh (tickets step),
                    // if tehere is time - find better solution
                }
                return {
                    ...bundle,
                    productType: PRODUCT_TYPE_TICKET,
                    productId,
                    cartItemId: itemsBundle
                        ? itemsBundle[0].cartItemId
                        : cartItemId,
                    detail: bundleDetail,
                }
            }
            return { ...itemsBundle, detail: {} as CartItemTicketDetailDto }
        })

    return [...ticketsBundles, ...tickets]
}

// filters data from cart api, searching for:
// - tickets to park bundled with other tickets and products, treated as different product
// === family package
export const filterStandaloneTicketsBundlesFromCart = (
    cartItems?: GetCartItemPresenter[]
): GetCartItemPresenter[] => {
    if (!cartItems) return []

    const ticketsBundles = cartItems?.filter(
        (cartItem: GetCartItemPresenter) => {
            const { productType, itemsBundle, name } = cartItem

            // - cart item has to be 'bundle' type
            if (productType !== PRODUCT_TYPE_BUNDLE) return false

            // TODO: figure out with backend how to add meningful key ids to different bundles
            // - actually, bundle name has to include 'family'
            if (
                !name?.toLowerCase().includes('family') &&
                !name?.toLowerCase().includes('сімейний')
            )
                return false

            // - and has to contain some tickets within its bundled items
            const hasBundledTicket = !!itemsBundle?.some(
                ({ productType }) => productType === PRODUCT_TYPE_TICKET
            )

            return hasBundledTicket
        }
    )

    return ticketsBundles
}

// filters data from cart api, searching for:
// - promotional tickets to park, preciselly for city break promotion
export const filterCityBreakTicketsFromCart = (
    cartItems?: GetCartItemPresenter[]
): Ticket[] =>
    cartItems
        ? (cartItems?.filter(
              ({ productId }) =>
                  productId &&
                  [
                      PRODUCT_ID_CITY_BREAK_TICKET_ADULT,
                      PRODUCT_ID_CITY_BREAK_TICKET_TEENAGER,
                  ].includes(productId)
          ) as Ticket[])
        : []

// filters data from cart api, searching for:
// - promotional vouchers to park, preciselly for march promotion
export const filterMarchPromoVouchersFromCart = (
    cartItems?: GetCartItemPresenter[]
): MarchVoucher[] =>
    cartItems
        ? (cartItems?.filter(
              ({ productId }) =>
                  productId &&
                  [
                      PRODUCT_ID_MARCH_VOUCHER_PROLONGED,
                      PRODUCT_ID_MARCH_VOUCHER_STANDARD,
                  ].includes(productId)
          ) as MarchVoucher[])
        : []
